/**
 * MasteryBadge - Reusable mastery level indicator
 *
 * Color-coded badges for different mastery levels:
 * - Mastered (green): >= 80%
 * - Developing (yellow): 50-79%
 * - Beginning (red): < 50%
 */

import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

// =============================================================================
// Types
// =============================================================================

export type MasteryLevel = 'mastered' | 'developing' | 'beginning';

export interface MasteryInfo {
  level: MasteryLevel;
  label: string;
  threshold: number;
  className: string;
}

// =============================================================================
// Constants
// =============================================================================

export const MASTERY_LEVELS: Record<MasteryLevel, MasteryInfo> = {
  mastered: {
    level: 'mastered',
    label: 'Mastered',
    threshold: 80,
    className: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300 border-green-200 dark:border-green-800',
  },
  developing: {
    level: 'developing',
    label: 'Developing',
    threshold: 50,
    className: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300 border-yellow-200 dark:border-yellow-800',
  },
  beginning: {
    level: 'beginning',
    label: 'Beginning',
    threshold: 0,
    className: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300 border-red-200 dark:border-red-800',
  },
};

// =============================================================================
// Helper Functions
// =============================================================================

/**
 * Get mastery level based on percentage score.
 */
export function getMasteryLevel(percentage: number): MasteryInfo {
  if (percentage >= 80) return MASTERY_LEVELS.mastered;
  if (percentage >= 50) return MASTERY_LEVELS.developing;
  return MASTERY_LEVELS.beginning;
}

/**
 * Get color for mastery percentage (for progress bars, etc.)
 */
export function getMasteryColor(percentage: number): string {
  if (percentage >= 80) return 'bg-green-500';
  if (percentage >= 50) return 'bg-yellow-500';
  return 'bg-red-500';
}

/**
 * Get text color for mastery percentage
 */
export function getMasteryTextColor(percentage: number): string {
  if (percentage >= 80) return 'text-green-600 dark:text-green-400';
  if (percentage >= 50) return 'text-yellow-600 dark:text-yellow-400';
  return 'text-red-600 dark:text-red-400';
}

// =============================================================================
// Component Props
// =============================================================================

export interface MasteryBadgeProps {
  /** Percentage score (0-100) */
  percentage: number;
  /** Show percentage value alongside label */
  showPercentage?: boolean;
  /** Additional class names */
  className?: string;
  /** Size variant */
  size?: 'sm' | 'default' | 'lg';
}

// =============================================================================
// Component
// =============================================================================

export function MasteryBadge({
  percentage,
  showPercentage = false,
  className,
  size = 'default',
}: MasteryBadgeProps) {
  const mastery = getMasteryLevel(percentage);

  const sizeClasses = {
    sm: 'text-xs px-1.5 py-0',
    default: 'text-xs px-2 py-0.5',
    lg: 'text-sm px-2.5 py-1',
  };

  return (
    <Badge
      variant="outline"
      className={cn(
        'font-medium border',
        mastery.className,
        sizeClasses[size],
        className
      )}
    >
      {showPercentage ? `${Math.round(percentage)}% - ${mastery.label}` : mastery.label}
    </Badge>
  );
}

export default MasteryBadge;
